import type { CanvasPoint, CurrentPoint, Point, Size } from '../../types/scene';
import { clamp } from '../data/numbers';

export interface BoxBounds extends Size, Point {}

export function boxCollides(b: BoxBounds, x: number, y: number, w: number, h: number): boolean {
    return x < b.x + b.width && x + w > b.x && y < b.y + b.height && y + h > b.y;
}

export function boxContains(b: BoxBounds, x: number, y: number, w: number = 0, h: number = 0): boolean {
    return x >= b.x && x + w <= b.x + b.width && y >= b.y && y + h <= b.y + b.height;
}

/** `b` shrunk by `inset` px on every side. Width/height may go non-positive for a small box; callers guard. */
export function insetBox(b: BoxBounds, inset: number): BoxBounds {
    return { x: b.x + inset, y: b.y + inset, width: b.width - 2 * inset, height: b.height - 2 * inset };
}

/** `b` shrunk by `insetX` on left/right and `insetY` on top/bottom. Width/height may go non-positive; callers guard. */
export function insetBoxXY(b: BoxBounds, insetX: number, insetY: number): BoxBounds {
    return { x: b.x + insetX, y: b.y + insetY, width: b.width - 2 * insetX, height: b.height - 2 * insetY };
}

export function boxEmpty(b: BoxBounds | undefined): boolean {
    return b == null || b.height === 0 || b.width === 0 || Number.isNaN(b.height) || Number.isNaN(b.width);
}

export function boxesEqual(a: BoxBounds | undefined, b: BoxBounds | undefined): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;
    return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

/** Convert absolute canvas point to a local point relative to `currentBounds`. */
export function toCurrentPoint(canvasPoint: CanvasPoint, currentBounds: BoxBounds | undefined): CurrentPoint {
    const { x: offsetX = 0, y: offsetY = 0 } = currentBounds ?? {};
    return {
        currentX: canvasPoint.canvasX - offsetX,
        currentY: canvasPoint.canvasY - offsetY,
    };
}

/**
 * Convert & clip current point to a canvas-point.
 *
 * @param currentPoint The local point to transform & clip.
 * @param canvasBounds Canvas-relative bounds. If `undefined`, then no transformation nor clipping occurs.
 * @return A canvas point, translated relative to `canvasBounds['x'|'y']` and clipped within those bounds when
 * applicable.
 */
export function toClippedCanvasPoint(currentPoint: CurrentPoint, canvasBounds: BoxBounds | undefined): CanvasPoint {
    let { currentX: canvasX, currentY: canvasY } = currentPoint;
    if (!canvasBounds) {
        return { canvasX, canvasY };
    }

    canvasX += canvasBounds.x;
    canvasY += canvasBounds.y;
    canvasX = clamp(canvasBounds.x, canvasX, canvasBounds.x + canvasBounds.width);
    canvasY = clamp(canvasBounds.y, canvasY, canvasBounds.y + canvasBounds.height);
    return { canvasX, canvasY };
}
